/**
 * Unified storage utilities for consistent quota display across the application.
 *
 * This module provides a single source of truth for storage-related calculations
 * and formatting. All storage quota displays should use these functions.
 */

// Canonical unit conversion constants
export const BYTES_PER_KB = 1024
export const BYTES_PER_MB = 1024 * 1024
export const BYTES_PER_GB = 1024 * 1024 * 1024
export const BYTES_PER_TB = 1024 * 1024 * 1024 * 1024
export const GB_PER_TB = 1024

/**
 * Convert a storage value from any unit to bytes (canonical unit).
 */
export function toBytes(value: number, unit: 'bytes' | 'kb' | 'mb' | 'gb'): number {
  if (!value || isNaN(value)) return 0

  switch (unit) {
    case 'bytes':
      return value
    case 'kb':
      return value * BYTES_PER_KB
    case 'mb':
      return value * BYTES_PER_MB
    case 'gb':
      return value * BYTES_PER_GB
    default:
      return value
  }
}

export interface FormatStorageOptions {
  /** Number of decimal places. Default: 2 */
  decimals?: number
  /** Remove trailing zeros (e.g., "5.00" -> "5"). Default: false */
  dynamicDecimals?: boolean
  /** Include unit suffix (GB/TB). Default: true */
  includeUnit?: boolean
  /** Auto-scale to TB when >= 1024 GB. Default: true */
  autoScale?: boolean
}

/**
 * Format bytes to a GB/TB display string.
 * This is the single source of truth for storage formatting.
 * Automatically scales to TB when value >= 1024 GB (unless autoScale is false).
 *
 * @example
 * formatStorageGB(5368709120) // "5.00 GB"
 * formatStorageGB(5368709120, { decimals: 0 }) // "5 GB"
 * formatStorageGB(5368709120, { dynamicDecimals: true }) // "5 GB"
 * formatStorageGB(5368709120, { includeUnit: false }) // "5.00"
 * formatStorageGB(21474836480000) // "20000 GB" -> "19.53 TB"
 */
export function formatStorageGB(bytes: number, options: FormatStorageOptions = {}): string {
  const { decimals = 2, dynamicDecimals = false, includeUnit = true, autoScale = true } = options

  if (!bytes || isNaN(bytes)) {
    return includeUnit ? '0 GB' : '0'
  }

  // Auto-scale down for small values
  if (autoScale && bytes > 0 && bytes < BYTES_PER_MB) {
    const kb = bytes / BYTES_PER_KB
    if (kb < 1) {
      return includeUnit ? `${Math.round(bytes)} Bytes` : `${Math.round(bytes)}`
    }
    return includeUnit ? `${kb.toFixed(decimals)} KB` : kb.toFixed(decimals)
  }
  if (autoScale && bytes > 0 && bytes < 10 * BYTES_PER_MB) {
    const mb = bytes / BYTES_PER_MB
    let formatted: string
    if (dynamicDecimals) {
      formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
      }).format(mb)
    } else {
      formatted = mb.toFixed(decimals)
    }
    return includeUnit ? `${formatted} MB` : formatted
  }

  const gb = bytes / BYTES_PER_GB

  // Auto-scale to TB when >= 1024 GB
  if (autoScale && gb >= GB_PER_TB) {
    const tb = gb / GB_PER_TB
    let formatted: string

    if (dynamicDecimals) {
      formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
      }).format(tb)
    } else {
      formatted = tb.toFixed(decimals)
    }

    return includeUnit ? `${formatted} TB` : formatted
  }

  let formatted: string

  if (dynamicDecimals) {
    // Use Intl.NumberFormat to remove trailing zeros
    formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    }).format(gb)
  } else {
    formatted = gb.toFixed(decimals)
  }

  return includeUnit ? `${formatted} GB` : formatted
}

/**
 * Calculate usage percentage with safe division.
 * Returns a value between 0 and 100.
 */
export function calcUsagePercent(usageBytes: number, quotaBytes: number): number {
  if (!quotaBytes || quotaBytes <= 0) return 0
  if (!usageBytes || usageBytes <= 0) return 0

  const percent = (usageBytes / quotaBytes) * 100
  return Math.min(100, Math.max(0, percent))
}

/**
 * Calculate usage ratio for progress bars (0 to 1).
 */
export function calcUsageRatio(usageBytes: number, quotaBytes: number): number {
  return calcUsagePercent(usageBytes, quotaBytes) / 100
}

export type UsageStatus = 'normal' | 'warning' | 'critical' | 'expired'

/**
 * Determine the usage status for styling purposes.
 *
 * @param percent - Usage percentage (0-100)
 * @param isExpired - Whether the storage package is expired
 * @param warningThreshold - Percentage threshold for warning state (default: 85)
 * @param criticalThreshold - Percentage threshold for critical state (default: 100)
 */
export function getUsageStatus(
  percent: number,
  options: {
    isExpired?: boolean
    warningThreshold?: number
    criticalThreshold?: number
  } = {},
): UsageStatus {
  const { isExpired = false, warningThreshold = 85, criticalThreshold = 100 } = options

  if (isExpired) return 'expired'
  if (percent >= criticalThreshold) return 'critical'
  if (percent >= warningThreshold) return 'warning'
  return 'normal'
}

/**
 * Get the CSS color class for a usage status.
 */
export function getUsageStatusColor(status: UsageStatus): string {
  switch (status) {
    case 'expired':
      return 'bg-tertiary'
    case 'critical':
      return 'bg-error'
    case 'warning':
      return 'bg-warning'
    case 'normal':
    default:
      return 'bg-btn-secondary'
  }
}

/**
 * Format a GB value directly to a display string.
 * Use this when the value is already in GB units.
 * Automatically scales to TB when value >= 1024 GB (unless autoScale is false).
 *
 * @example
 * formatGB(5) // "5.00 GB"
 * formatGB(5, { decimals: 0 }) // "5 GB"
 * formatGB(5, { includeUnit: false }) // "5.00"
 * formatGB(20000) // "19.53 TB"
 * formatGB(20000, { autoScale: false }) // "20000.00 GB"
 */
export function formatGB(gb: number, options: FormatStorageOptions = {}): string {
  const { decimals = 2, dynamicDecimals = false, includeUnit = true, autoScale = true } = options

  if (!gb || isNaN(gb)) {
    return includeUnit ? '0 GB' : '0'
  }

  // Auto-scale to TB when >= 1024 GB
  if (autoScale && gb >= GB_PER_TB) {
    const tb = gb / GB_PER_TB
    let formatted: string

    if (dynamicDecimals) {
      formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
      }).format(tb)
    } else {
      formatted = tb.toFixed(decimals)
    }

    return includeUnit ? `${formatted} TB` : formatted
  }

  let formatted: string
  if (dynamicDecimals) {
    formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    }).format(gb)
  } else {
    formatted = gb.toFixed(decimals)
  }

  return includeUnit ? `${formatted} GB` : formatted
}

// Re-export getEffectiveQuota for convenience
export { getEffectiveQuota } from './volume'
